import { useQueryClient } from '@tanstack/react-query'
import { useContext, useState } from 'react'

import NAVAssignmentDropdown from 'components/brokers/needs_attention/NAVAssignmentDropdown'
import {
  filterByLabelAndExactValueCallback,
  setQueryDataCallback,
} from 'components/brokers/needs_attention/utils'
import BrokerContext from 'components/context/BrokerContext'
import SubstantiationContext from 'components/context/SubstantiationContext'
import { useGetBrokers } from 'components/hooks/needs_attention/useGetBrokers'
import ComboboxMultiSelectFilter, {
  type ComboboxFilterOption,
} from 'components/reusable/table/ComboboxMultiSelectFilter'
import { usePatchAdminSubstantiations } from 'utils/web_api'
import { type BrokersBroker } from 'utils/web_api.schemas'

type AssignBrokersToSubstantiationProps = {
  id: string
  assignedBrokers: BrokersBroker[]
}

const AssignBrokersToSubstantiation = ({
  id,
  assignedBrokers,
}: AssignBrokersToSubstantiationProps) => {
  const queryClient = useQueryClient()
  const { getSubstantiationsQueryKey } = useContext(SubstantiationContext)
  const { currentBroker } = useContext(BrokerContext)
  const [query, setQuery] = useState('')

  const { data } = useGetBrokers()
  const brokers = data?.data as BrokersBroker[]

  const pinnedBrokerOptions: ComboboxFilterOption<string>[] = [
    ...(currentBroker
      ? [{ label: currentBroker?.broker_name, value: currentBroker?.id }]
      : []),
  ]

  const brokerOptions: ComboboxFilterOption<string>[] =
    brokers
      ?.map((broker) => ({
        label: broker.broker_name,
        value: broker.id,
      }))
      .filter((option) => option.value !== currentBroker?.id) || []

  const assignBrokersToSubstantiationMutation2 = usePatchAdminSubstantiations()

  const assignedBrokerIds = assignedBrokers.map((broker) => broker.id)

  const handleSelectBroker = (brokerId: string) => {
    assignBrokersToSubstantiationMutation2.mutate(
      {
        data: {
          substantiation_ids: [id],
          ...(assignedBrokerIds.includes(brokerId)
            ? { broker_ids_to_remove: [brokerId] }
            : { broker_ids_to_add: [brokerId] }),
        },
      },
      {
        onSuccess: (data) => {
          // Update cached data with new substantiation items that include updated broker assignments.
          queryClient.setQueryData(
            getSubstantiationsQueryKey,
            setQueryDataCallback(data)
          )
        },
      }
    )
  }

  const filteredPinnedBrokerOptions = pinnedBrokerOptions.filter(
    filterByLabelAndExactValueCallback(query)
  )
  const filteredBrokerOptions = brokerOptions.filter(
    filterByLabelAndExactValueCallback(query)
  )

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <ComboboxMultiSelectFilter<string>
        selectedValues={assignedBrokerIds}
        options={[
          { groupLabel: 'Pinned', options: filteredPinnedBrokerOptions },
          { groupLabel: 'Brokers', options: filteredBrokerOptions },
        ]}
        onSelectValue={handleSelectBroker}
        placeholder='Search brokers to assign...'
        onValueChange={setQuery}
      >
        <NAVAssignmentDropdown
          noSelectionText='Assignee'
          pluralIdentifier='assignees'
          selectedValues={assignedBrokerIds}
          options={[...pinnedBrokerOptions, ...brokerOptions]}
          isPending={assignBrokersToSubstantiationMutation2.isPending}
          type='assignee'
        />
      </ComboboxMultiSelectFilter>
    </div>
  )
}

export default AssignBrokersToSubstantiation
